import { Popover } from '@byecode/ui'
import type { BackgroundProtocol, BackgroundType } from '@lighthouse/core'
import { BACKGROUND_TYPE, type VariableADTvalue } from '@lighthouse/core'
import { fieldConvertValue, FilledInput, FillPickerPopoverDropdown, useAtomData } from '@lighthouse/shared'
import React, { useCallback, useId, useMemo } from 'react'

import { lastPageOfStackAtom, pageAtomFamily } from '@/atoms/page/state'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'
import { useGetImageVariableOptions } from '@/hooks/useGetImageVariableOptions'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { uploadManagerInAppParams } from '@/utils/auth'

interface BorderFieldProps {
    label?: React.ReactNode
    value: BackgroundProtocol | undefined
    customViewDsId?: string
    enabledBackgroundTypes?: BackgroundType[]
    onChange: (val: BackgroundProtocol) => void
}

export const BackgroundField: React.FC<BorderFieldProps> = ({ label, customViewDsId, value, enabledBackgroundTypes, onChange }) => {
    const [_, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )
    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name || '', [])
    )

    const parseVariable = useParseVariableWithSubscription(value?.image?.resource)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field, { useFileUrl: true })
        },
        [parseVariable]
    )

    const uploadId = useId()
    const uploadProps = useMemo(
        () => ({
            accept: 'image/*',
            multiple: false,
            uploadOptions: {
                info: { id: uploadId, label: pageName, groupId: pageId },
                options: {
                    ...uploadManagerInAppParams()
                }
            }
        }),
        [pageName, pageId, uploadId]
    )

    // const imageTypeOption = useGetImageVariableOptions({
    //     customViewDsId
    // })

    const variableTypeOption = useGetColorVariableOptions({
        customViewDsId
    })

    const colorSystemMethods = useColorSystemAction()

    return (
        <Popover width={300} withinPortal position="left-start" autoUpdate offsetOptions={168}>
            <Popover.Target>
                <FilledInput
                    value={value}
                    getImageVariableUrl={parseVariable2String}
                    imageTypeOption={variableTypeOption}
                    variableTypeOption={variableTypeOption}
                    onClear={() => {
                        onChange({ type: enabledBackgroundTypes?.[0] || BACKGROUND_TYPE.color })
                    }}
                    style={{ width: 180 }}
                />
            </Popover.Target>
            <FillPickerPopoverDropdown
                title={label || '背景'}
                value={value}
                onChange={onChange}
                enabledBackgroundTypes={
                    enabledBackgroundTypes || [
                        BACKGROUND_TYPE.color,
                        BACKGROUND_TYPE.gradient,
                        BACKGROUND_TYPE.image,
                        BACKGROUND_TYPE.variable
                    ]
                }
                uploadProps={uploadProps}
                imageTypeOption={variableTypeOption}
                variableTypeOption={variableTypeOption}
                {...colorSystemMethods}
            />
        </Popover>
    )
}
